import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Types for Document Builder API
export interface CreateDocumentProjectRequest {
    name: string;
    documentType: string;
    userInput?: {
        prompt?: string;
        fields?: Record<string, string>;
        additionalContext?: string;
    };
}

export interface CreateDocumentProjectResponse {
    projectId: string;
    name: string;
    documentType: string;
    status: string;
    createdAt: string;
}

export interface GenerateDocumentRequest {
    projectId: string;
    regenerate?: boolean;
}

export interface GenerateDocumentResponse {
    documentPlan?: any;
    generatedDocumentCode?: string;
    documentGenerationSession?: {
        status: string;
        generatedAt?: string;
        s3Url?: string;
        version?: number;
        error?: string;
    };
    __output?: {
        meta: {
            agent: string;
            version: string;
            generatedAt?: string;
            status?: string;
        };
        document?: {
            url: string;
            fileName: string;
            type: string;
            sizeBytes: number;
        };
        error?: string;
        recommendation: {
            nextStep: string;
            reason: string;
        };
    };
    meta?: {
        cached?: boolean;
    };
}

export interface ChatMessage {
    role: 'user' | 'ai';
    content: string;
}

export interface ChatWithAssistantRequest {
    documentType: string;
    messages: ChatMessage[];
    collectedInfo?: Record<string, string>;
}

export interface ChatWithAssistantResponse {
    message: string;
    collectedInfo: Record<string, string>;
    isComplete: boolean;
    missingFields: string[];
    documentType: string;
}

export interface EditDocumentRequest {
    projectId: string;
    editRequest: string;
}

export interface EditDocumentResponse {
    updatedSections: string[];
    document: {
        url: string;
        fileName: string;
        type: string;
        sizeBytes: number;
        version: number;
    };
    editRequest: string;
    editedAt: string;
}

export interface GetDocumentProjectRequest {
    projectId: string;
}

export interface GetDocumentProjectResponse {
    projectId: string;
    name: string;
    documentType: string;
    status: string;
    userInput: {
        prompt?: string;
        fields?: Record<string, string>;
        additionalContext?: string;
    };
    documentPlan?: any;
    generatedDocument?: {
        url: string;
        fileName: string;
        version: number;
        generatedAt: string;
        fileSizeBytes: number;
    };
    createdAt: string;
    updatedAt: string;
}

export interface GetAllDocumentProjectsParams {
    page?: number;
    limit?: number;
    search?: string;
}

export interface GetAllDocumentProjectsResponse {
    statusCode: number;
    data: {
        projects: any[];
        total: number;
    };
    message: string;
    success: boolean;
}

// Version History Types
export interface VersionHistoryItem {
    version: number;
    s3Url: string;
    fileName: string;
    editedAt: string;
    fileSizeBytes?: number;
    editRequest?: string;
    isCurrent: boolean;
}

export interface GetVersionHistoryResponse {
    versions: VersionHistoryItem[];
}

export interface RestoreVersionRequest {
    projectId: string;
    version: number;
}

export interface RestoreVersionResponse {
    document: {
        url: string;
        fileName: string;
        version: number;
        fileSizeBytes: number;
        restoredFrom: number;
    };
    codeRestored: boolean;
    restoredAt: string;
}

export const documentBuilderApi = createApi({
    reducerPath: 'documentBuilderApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.NEXT_PUBLIC_API_URL}/document-builder`,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth.token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Document', 'VersionHistory'],
    endpoints: (builder) => ({
        // Create a new document project
        createDocumentProject: builder.mutation<CreateDocumentProjectResponse, CreateDocumentProjectRequest>({
            query: (data) => ({
                url: '/create',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data: CreateDocumentProjectResponse }) => response.data,
        }),

        // Generate document from project
        generateDocument: builder.mutation<GenerateDocumentResponse, GenerateDocumentRequest>({
            query: (data) => ({
                url: '/generate',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data: GenerateDocumentResponse }) => response.data,
        }),

        // Chat with AI assistant
        chatWithAssistant: builder.mutation<ChatWithAssistantResponse, ChatWithAssistantRequest>({
            query: (data) => ({
                url: '/chat',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data: ChatWithAssistantResponse }) => response.data,
        }),

        // Edit document - partial update of specific sections
        editDocument: builder.mutation<EditDocumentResponse, EditDocumentRequest>({
            query: (data) => ({
                url: '/edit',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data: EditDocumentResponse }) => response.data,
        }),

        // Get existing document project
        getDocumentProject: builder.query<GetDocumentProjectResponse, GetDocumentProjectRequest>({
            query: ({ projectId }) => ({
                url: `/projects/${projectId}`,
                method: 'GET',
            }),
            transformResponse: (response: { data: GetDocumentProjectResponse }) => response.data,
        }),

        // Get all document projects
        getAllDocumentProjects: builder.query<GetAllDocumentProjectsResponse, GetAllDocumentProjectsParams | void>({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params?.page) queryParams.append('page', params.page.toString());
                if (params?.limit) queryParams.append('limit', params.limit.toString());
                if (params?.search) queryParams.append('search', params.search);
                const queryString = queryParams.toString();
                return `/list${queryString ? `?${queryString}` : ''}`;
            },
            transformResponse: (response: { data: { projects: any[]; total: number } }) => ({
                statusCode: 200,
                data: response.data,
                message: 'Success',
                success: true,
            }),
            providesTags: ['Document'],
        }),

        // Get version history for a document project
        getVersionHistory: builder.query<GetVersionHistoryResponse, string>({
            query: (projectId) => `/projects/${projectId}/versions`,
            transformResponse: (response: { data: GetVersionHistoryResponse }) => response.data,
            providesTags: (_result, _error, projectId) => [{ type: 'VersionHistory', id: projectId }],
        }),

        // Restore a previous version
        restoreVersion: builder.mutation<RestoreVersionResponse, RestoreVersionRequest>({
            query: ({ projectId, version }) => ({
                url: `/projects/${projectId}/restore`,
                method: 'POST',
                body: { version },
            }),
            transformResponse: (response: { data: RestoreVersionResponse }) => response.data,
            invalidatesTags: (_result, _error, { projectId }) => [{ type: 'VersionHistory', id: projectId }],
        }),

    }),
});

export const {
    useCreateDocumentProjectMutation,
    useGenerateDocumentMutation,
    useChatWithAssistantMutation,
    useEditDocumentMutation,
    useGetDocumentProjectQuery,
    useGetAllDocumentProjectsQuery,
    useGetVersionHistoryQuery,
    useRestoreVersionMutation,
} = documentBuilderApi;

// Re-export brand kit hooks from pitchDeckApi for use with documents
// Use: import { useGetBrandKitsQuery, useAssignBrandKitToProjectMutation } from '@/app/store/api/pitchDeckApi'
